import { numberToLetters } from '../../../../utils/letterNumber';
import { AUTOCAL_STATUS } from '../../../../types/Session';

// Shared calibration-bar colour coding and severity logic.
// Imported by SessionCalibrations (the calibration bar), SessionStatusBar
// (the session status box) and CalibrationListGrid (the calibration dots) so
// the surfaces stay on an identical colour scheme and identical thresholds.

export enum CalibrationSeverity {
  GRAY = 'gray',
  GREEN = 'green',
  YELLOW = 'yellow',
  RED = 'red',
}

export const SEVERITY_CONFIG: Record<
  CalibrationSeverity,
  { bg: string; border: string; label: string; rank: number }
> = {
  [CalibrationSeverity.GRAY]: { bg: '#f3f4f6', border: '#03365D', label: 'No Autocalibration', rank: 0 },
  [CalibrationSeverity.GREEN]: { bg: '#dcfce7', border: '#14532d', label: 'Autocalibration Good', rank: 1 },
  [CalibrationSeverity.YELLOW]: { bg: '#fefce8', border: '#ca8a04', label: 'Autocalibration Warning, expand for details', rank: 2 },
  [CalibrationSeverity.RED]: { bg: '#fef2f2', border: '#dc2626', label: 'Autocalibration Error, values out of range', rank: 3 },
};

export const CP_CORR_DEFAULT = 1.0299;
export const YAW_OFFSET_DEFAULT = 0.0;
export const DEFAULT_EPSILON = 0.001;

export function isDefault(value: number, defaultValue: number): boolean {
  return Math.abs(value - defaultValue) < DEFAULT_EPSILON;
}

export function getCpCorrSeverity(v: number): CalibrationSeverity {
  if (v > 1.02 && v < 1.25) return CalibrationSeverity.GREEN;
  if ((v > 1.0 && v <= 1.02) || (v >= 1.25 && v < 1.5)) return CalibrationSeverity.YELLOW;
  return CalibrationSeverity.RED;
}

export function getYawOffsetSeverity(v: number): CalibrationSeverity {
  const abs = Math.abs(v);
  if (abs < 4) return CalibrationSeverity.GREEN;
  if (abs <= 7) return CalibrationSeverity.YELLOW;
  return CalibrationSeverity.RED;
}

// Autocalibrate state cue, from testsessions.autocal_status. Shared by the
// calibration bar and the session status box so both show the same wording:
// PENDING means inputs changed and the stored calibration is stale.
// There is no separate 'running' cue any more — the server dropped
// AutocalStatus.RUNNING when autocalibrate moved onto the jobs queue, because
// that value was a worker's lease living on the session row. PENDING now covers
// queued and running alike, which is accurate: the calibration is stale in both.
export function getAutocalCue(session: any): { note: string | null; pending: boolean; failed: boolean } {
  const status = session?.autocal_status;
  if (status === AUTOCAL_STATUS.PENDING) return { note: 'Autocal due — not yet run', pending: true, failed: false };
  if (status === AUTOCAL_STATUS.ERROR) {
    const suffix = session?.autocal_error ? ` — ${session.autocal_error}` : '';
    return { note: `Autocal failed${suffix}`, pending: false, failed: true };
  }
  return { note: null, pending: false, failed: false };
}

// Configs that feed the calibration bar: only those flagged branch = true.
export function getConfigsWithBranch(session: any): any[] {
  return session?.configs?.filter((config: any) => config?.branch === true) || [];
}

export function getCalibrationBarSeverity(configs: any[]): { severity: CalibrationSeverity; tooltip: string } {
  if (!configs || configs.length === 0) {
    return { severity: CalibrationSeverity.GRAY, tooltip: SEVERITY_CONFIG[CalibrationSeverity.GRAY].label };
  }

  const allDefault = configs.every((c: any) => {
    const cp = c.cp_corr ?? CP_CORR_DEFAULT;
    const yaw = c.yaw_offset ?? YAW_OFFSET_DEFAULT;
    return Math.abs(cp - CP_CORR_DEFAULT) < DEFAULT_EPSILON && Math.abs(yaw - YAW_OFFSET_DEFAULT) < DEFAULT_EPSILON;
  });

  if (allDefault) {
    return { severity: CalibrationSeverity.GRAY, tooltip: SEVERITY_CONFIG[CalibrationSeverity.GRAY].label };
  }

  let worst: CalibrationSeverity = CalibrationSeverity.GREEN;
  // Identical messages are grouped so one line names every affected config,
  // e.g. "Configs A, J, K: cp_corr 1.0081 in marginal range", instead of one
  // line per config repeating the same text.
  const issueConfigs = new Map<string, string[]>();
  const addIssue = (message: string, configLabel: string) => {
    const labels = issueConfigs.get(message) ?? [];
    labels.push(configLabel);
    issueConfigs.set(message, labels);
  };

  configs.forEach((c: any) => {
    const label = numberToLetters(c.letter_number ?? 0);
    const cp = c.cp_corr ?? CP_CORR_DEFAULT;
    const yaw = c.yaw_offset ?? YAW_OFFSET_DEFAULT;

    const cpDefault = Math.abs(cp - CP_CORR_DEFAULT) < DEFAULT_EPSILON;
    const yawDefault = Math.abs(yaw - YAW_OFFSET_DEFAULT) < DEFAULT_EPSILON;
    if (cpDefault && yawDefault) return;

    const cpSev = cpDefault ? CalibrationSeverity.GREEN : getCpCorrSeverity(cp);
    const yawSev = yawDefault ? CalibrationSeverity.GREEN : getYawOffsetSeverity(yaw);

    if (SEVERITY_CONFIG[cpSev].rank > SEVERITY_CONFIG[worst].rank) worst = cpSev;
    if (SEVERITY_CONFIG[yawSev].rank > SEVERITY_CONFIG[worst].rank) worst = yawSev;

    if (cpSev === CalibrationSeverity.YELLOW) addIssue(`cp_corr ${cp.toFixed(4)} in marginal range`, label);
    if (cpSev === CalibrationSeverity.RED) addIssue(`cp_corr ${cp.toFixed(4)} out of range`, label);
    if (yawSev === CalibrationSeverity.YELLOW) addIssue(`yaw_offset ${yaw.toFixed(2)} in marginal range (4–7)`, label);
    if (yawSev === CalibrationSeverity.RED) addIssue(`yaw_offset ${yaw.toFixed(2)} exceeds safe range (>7)`, label);
  });

  const issues = Array.from(issueConfigs.entries()).map(([message, labels]) => {
    const prefix = labels.length === 1 ? `Config ${labels[0]}` : `Configs ${labels.join(', ')}`;
    return `${prefix}: ${message}`;
  });

  const tooltip =
    issues.length > 0
      ? `${SEVERITY_CONFIG[worst].label}:\n${issues.join('\n')}`
      : SEVERITY_CONFIG[worst].label;

  return { severity: worst, tooltip };
}
